/**
 * Domain Pricing Card Component (P4-B3)
 *
 * Displays hydrated pricing for BIN + LTO options.
 * Shows skeletons during loading and graceful fallbacks for missing prices.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShoppingCart, CreditCard, Info } from 'lucide-react';
import { useDomainPricing, formatPrice, formatMonthlyPrice } from '@/hooks/useDomainPricing';
import { fireEvent } from '@/lib/analytics';

interface DomainPricingCardProps {
  domain: string;
  onAction?: (action: 'bin' | 'lto_12' | 'lto_24' | 'lto_36' | 'contact') => void;
}

export default function DomainPricingCard({ domain, onAction }: DomainPricingCardProps) {
  const { pricing, loading, error, notConfigured } = useDomainPricing(domain);

  const handleCTAClick = (action: 'bin' | 'lto_12' | 'lto_24' | 'lto_36' | 'contact') => {
    fireEvent('cta_clicked', { domain, action: action.toUpperCase() });
    onAction?.(action);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="max-w-3xl mx-auto rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle>Pricing</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>
              Could not load pricing. Please refresh or contact support.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (notConfigured || !pricing) {
    return (
      <Card className="max-w-3xl mx-auto rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle>Pricing</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Pricing for this domain is available upon request.{' '}
              <Button
                variant="link"
                className="h-auto p-0 underline font-medium"
                onClick={() => handleCTAClick('contact')}
              >
                Contact us
              </Button>{' '}
              for a quote.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto rounded-2xl shadow-md">
      <CardHeader>
        <CardTitle>Pricing Options</CardTitle>
        <p className="text-sm text-muted-foreground mt-2">
          Every option below gives you full control of {domain}. Choose what fits your stage and budget.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Buy It Now */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Buy It Now</h3>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="text-2xl font-bold">{formatPrice(pricing.binAmount)}</p>
              <p className="text-sm text-muted-foreground">
                One-time payment for immediate, permanent ownership.
              </p>
            </div>
            <Button onClick={() => handleCTAClick('bin')} size="lg">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Buy Now
            </Button>
          </div>
        </div>

        {/* Lease-to-Own Options */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Lease-to-Own</h3>
          <p className="text-xs text-muted-foreground">
            Spread payments over time while using {domain} in your brand, website, and email. Ownership transfers once your final payment clears.
          </p>

          {/* 12-month option */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="text-xl font-bold">{formatPrice(pricing.ltoMonthly12)}/mo</p>
              <p className="text-sm text-muted-foreground">12 months · {formatPrice(pricing.ltoMonthly12 * 12)} total</p>
            </div>
            <Button onClick={() => handleCTAClick('lto_12')} variant="outline" size="lg" className="apple-button-secondary-cta">
              <CreditCard className="w-4 h-4 mr-2" />
              Lease 12mo
            </Button>
          </div>

          {/* 24-month option */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="text-xl font-bold">{formatPrice(pricing.ltoMonthly24)}/mo</p>
              <p className="text-sm text-muted-foreground">24 months · {formatPrice(pricing.ltoMonthly24 * 24)} total</p>
            </div>
            <Button onClick={() => handleCTAClick('lto_24')} variant="outline" size="lg" className="apple-button-secondary-cta">
              <CreditCard className="w-4 h-4 mr-2" />
              Lease 24mo
            </Button>
          </div>

          {/* 36-month option */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="text-xl font-bold">{formatPrice(pricing.ltoMonthly36)}/mo</p>
              <p className="text-sm text-muted-foreground">36 months · {formatPrice(pricing.ltoMonthly36 * 36)} total</p>
            </div>
            <Button onClick={() => handleCTAClick('lto_36')} variant="outline" size="lg" className="apple-button-secondary-cta">
              <CreditCard className="w-4 h-4 mr-2" />
              Lease 36mo
            </Button>
          </div>
        </div>

        {/* Help text */}
        <p className="text-xs text-muted-foreground text-center">
          Lease-to-own: Use your domain immediately, own it after final payment.{' '}
          <a href="/faqs" className="underline">
            Learn more
          </a>
        </p>
      </CardContent>
    </Card>
  );
}

